import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "./ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ug-detailing-shop/artifacts/tsv9bqsz_1.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Diensten", path: "/diensten" },
  { name: "Producten", path: "/producten" },
  { name: "Offerte", path: "/offerte" },
  { name: "Contact", path: "/contact" },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
        isScrolled ? "glass-heavy" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="UG-Detailing"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm font-medium tracking-wider uppercase transition-colors duration-300 link-underline ${
                  location.pathname === link.path
                    ? "text-primary"
                    : "text-gray-300 hover:text-white"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="https://wa.me/31623861746"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-gray-300 hover:text-white transition-colors duration-300"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4 mr-2" />
              <span className="text-sm">06 23 86 17 46</span>
            </a>
            <Link to="/boeken">
              <Button
                data-testid="book-now-btn"
                className="bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold px-6 py-2"
              >
                Boeken
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden glass-heavy border-t border-white/10"
        >
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className={`block text-lg font-medium tracking-wider uppercase ${
                  location.pathname === link.path
                    ? "text-primary"
                    : "text-gray-300"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 border-t border-white/10">
              <Link to="/boeken" className="block">
                <Button
                  data-testid="mobile-book-btn"
                  className="w-full bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold py-3"
                >
                  Boeken
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
